import React, { useState, useEffect } from 'react';
import { X, Bell, BellOff } from 'lucide-react';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NotificationSettings = ({ onClose }) => {
  const [settings, setSettings] = useState({
    email_notifications: true,
    friend_requests: true,
    new_messages: true,
    ai_advisor_updates: true,
    study_reminders: false,
    scholarship_alerts: true
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/profile/notification-settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.data.settings) {
        setSettings(response.data.settings);
      }
    } catch (error) {
      console.error('Failed to load notification settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (key) => {
    const newSettings = { ...settings, [key]: !settings[key] };
    setSettings(newSettings);
    
    // Auto-save after toggle
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/profile/notification-settings`,
        { settings: newSettings },
        { headers: { 'Authorization': `Bearer ${token}` }}
      );
    } catch (error) {
      console.error('Failed to save setting:', error);
      // Revert on error
      setSettings(settings);
      toast.error('Failed to update setting');
    }
  };

  const notificationOptions = [
    {
      key: 'email_notifications',
      title: 'Email Notifications',
      description: 'Receive important updates via email',
      icon: <Bell className="h-5 w-5" />
    },
    {
      key: 'friend_requests',
      title: 'Friend Requests',
      description: 'Get notified when someone sends you a friend request',
      icon: <Bell className="h-5 w-5" />
    },
    {
      key: 'new_messages',
      title: 'New Messages',
      description: 'Notifications for new chat messages',
      icon: <Bell className="h-5 w-5" />
    },
    {
      key: 'ai_advisor_updates',
      title: 'AI Advisor Updates',
      description: 'Updates and tips from your learning buddy',
      icon: <Bell className="h-5 w-5" />
    },
    {
      key: 'study_reminders',
      title: 'Study Reminders',
      description: 'Daily reminders to stay on track',
      icon: <Bell className="h-5 w-5" />
    },
    {
      key: 'scholarship_alerts',
      title: 'Scholarship & Fellowship Alerts',
      description: 'Get notified about relevant opportunities',
      icon: <Bell className="h-5 w-5" />
    }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                <Bell className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">Notification Settings</h2>
                <p className="text-sm text-gray-500">Manage your notification preferences</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="text-gray-500 mt-4">Loading settings...</p>
            </div>
          ) : (
            <div className="space-y-4">
              {notificationOptions.map((option) => (
                <div
                  key={option.key}
                  className="flex items-center justify-between p-4 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-start space-x-4">
                    <div className="text-gray-600 mt-1">
                      {option.icon}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900">{option.title}</h3>
                      <p className="text-sm text-gray-500 mt-1">{option.description}</p>
                    </div>
                  </div>
                  <Switch
                    checked={settings[option.key]}
                    onCheckedChange={() => handleToggle(option.key)}
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-500">
              Changes are saved automatically
            </p>
            <Button onClick={onClose} className="bg-blue-700 hover:bg-blue-700">
              Done
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationSettings;
